import { addAppListener, AppStartListening } from './listenerMiddleware'
import {
  AuthenticationActions,
  selectMuid, getProviderName
} from 'shared/redux/authentication/authenticationSlice'
import { AppThunk } from './store'
import { Hub } from 'aws-amplify'
import { setUpProductAuthListener } from 'shared/redux/product/ProductSlice'
import AnalyticsManager from '../services/AnalyticsManager'
import { getUserEmail } from 'shared/services/AuthenticationService'
import { logFirebaseEvent } from '../utils/firebaseConfig'
import { logCustomMetapixelEvent } from '../utils/MetapixelManager'
export const setUpOauthListener =
  (): AppThunk<Promise<string>> => (dispatch) => {
    let destination = '/'
    const unsubscribeFromHub = Hub.listen(
      'auth',
      ({ payload: { event, data } }) => {
        if (event === 'customOAuthState' && data) {
          destination = data
        }
      },
    )
    return new Promise((resolve, reject) => {
      dispatch(
        addAppListener({
          actionCreator: AuthenticationActions.addBlockingRequest,
          effect: async (action, listenerApi) => {
            if (action.payload !== 'OAUTH') {
              return
            }
            try {
              // If muid is already populated (e.g. listener registered after Hub event fired),
              // resolve immediately without waiting.
              if (selectMuid(listenerApi.getState())) {
                const provider = await getProviderName()
                const email = await getUserEmail()
                await AnalyticsManager.registerSuperProperties({
                  "email": email
                })
                AnalyticsManager.trackTypedEvent(
                  {
                    event: 'Sign In Federated - Web',
                    source: 'Web App',
                    provider: provider
                  }
                )
                logFirebaseEvent("Sign In Federated - Web", {"provider": provider, "email": email})
                logCustomMetapixelEvent("Sign In Federated - Web", {"provider": provider, "email": email})
                resolve(destination)
                listenerApi.unsubscribe()
                unsubscribeFromHub()
                listenerApi.dispatch(
                  AuthenticationActions.removeBlockingRequest('OAUTH'),
                )
                return
              }

              // Fallback: If the Amplify signIn Hub event fired before createAmplifyListener
              // was registered, muid won't be set. Manually check the authenticated session
              // and dispatch postConfirmLogin to populate Redux state.
              import('aws-amplify').then(({ Auth }) => {
                Auth.currentAuthenticatedUser({ bypassCache: true })
                  .then((user) => {
                    import('shared/redux/authentication/authenticationSlice').then(({ postConfirmLogin }) => {
                      listenerApi.dispatch(postConfirmLogin(user) as any).catch(() => {
                        // Fallback postConfirmLogin failed — condition timer will handle rejection
                      })
                    })
                  })
                  .catch(() => {
                    // No authenticated user yet — condition timer will handle rejection
                  })
              })

              const wasSuccessful = await listenerApi.condition(
                (_, state) => !!selectMuid(state),
                10000,
              )
              if (wasSuccessful) {
                const provider = await getProviderName()
                const email = await getUserEmail()
                await AnalyticsManager.registerSuperProperties({
                  "email": email
                })
                AnalyticsManager.trackTypedEvent(
                  {
                    event: 'Sign In Federated - Web',
                    source: 'Web App',
                    provider: provider
                  }
                )
                logFirebaseEvent("Sign In Federated - Web", {"provider": provider, "email": email})
                logCustomMetapixelEvent("Sign In Federated - Web", {"provider": provider, "email": email})
                resolve(destination)
              } else {
                reject('/login')
              }
            } catch (error) {
              reject('/login')
            }
            listenerApi.unsubscribe()
            unsubscribeFromHub()
            listenerApi.dispatch(
              AuthenticationActions.removeBlockingRequest('OAUTH'),
            )
          },
        }),
      )

      // and now that we're listening, we can trigger it ourselves
      dispatch(AuthenticationActions.addBlockingRequest('OAUTH'))
    })
  }

export const setUpExtraListeners = (listen: AppStartListening) => {
  setUpProductAuthListener(listen)
}
